import { AssociationsSection, CustomPropertiesSection, HistoryButton, RecordTags } from './records';
import { usePermission } from '@/hooks/use-permission';
import { RecordSection } from '@/components/shared/record-section';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { AiRecordSummary } from '@/features/ai/ai';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  Briefcase,
  Pencil,
  Phone,
  Plus,
  StickyNote,
} from 'lucide-react';
import { PageHeader, ErrorState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, Select, Textarea } from '@/components/ui/misc';
import { formatDate, formatDateTime, formatMoney, timeAgo } from '@/lib/format';
import { useDeal, useLogActivity, usePipelines, useSaveTask, useTimeline } from './api';
import { RecordSalesActivity, SalesToolbar } from '@/features/sales/record-tools';
import { LinkedObjectsCard } from '@/features/objects/objects-page';
import { useLogCall } from '@/features/sales/api';
import {
  DEAL_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  QUOTE_STATUS_LABELS,
  QUOTE_STATUS_VARIANT,
  TASK_PRIORITY_VARIANT,
  TASK_TYPE_LABELS,
} from './labels';
import { DealDialog } from './deal-dialog';
import { MoveDealDialog } from './move-deal-dialog';
import type { Deal, DealStageHistoryEntry, PipelineStage, QuoteStatus } from './types';

type DealDetail = Deal & {
  stageHistory: DealStageHistoryEntry[];
  quotes: {
    id: string;
    number: string;
    version: number;
    status: QuoteStatus;
    grandTotal: string | null;
    currency: string;
    issueDate: string;
    validUntil: string | null;
  }[];
};

export function DealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { data, isLoading, error, refetch } = useDeal(dealId);
  const { data: pipelines = [] } = usePipelines();
  const [editing, setEditing] = useState(false);
  const [movingTo, setMovingTo] = useState<PipelineStage | null>(null);
  const [tab, setTab] = useState<'activities' | 'overview'>('activities');
  const { can } = usePermission();
  const t = useT();

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Deal" />
        <TableSkeleton />
      </div>
    );
  }
  if (error || !data) {
    return <ErrorState message="Could not load this deal." onRetry={() => void refetch()} />;
  }

  const deal = data as DealDetail;
  const stages = pipelines.find((pipeline) => pipeline.id === deal.pipelineId)?.stages ?? [];

  return (
    <div className="space-y-4">
      {/* The pipeline as a path: where the deal is and where it can go next. */}
      {deal.status === 'OPEN' && stages.length > 0 && (
        <div className="scrollbar-thin flex overflow-x-auto rounded-lg border bg-card p-1.5">
          {stages.map((stage, index) => {
            const current = stage.id === deal.stageId;
            const passed = stages.findIndex((item) => item.id === deal.stageId) > index;
            return (
              <button
                key={stage.id}
                disabled={current}
                onClick={() => setMovingTo(stage)}
                title={current ? undefined : `Move to ${stage.name}`}
                className={cn(
                  'relative min-w-[8rem] flex-1 truncate px-4 py-2 text-xs font-bold transition-colors first:rounded-s-md last:rounded-e-md',
                  current ? 'text-white' : passed ? 'bg-muted text-foreground hover:bg-accent' : 'text-muted-foreground hover:bg-muted',
                )}
                style={current ? { backgroundColor: stage.colour } : undefined}
              >
                {stage.name}
              </button>
            );
          })}
        </div>
      )}

      {deal.status !== 'OPEN' && (
        <div
          className={
            deal.status === 'WON'
              ? 'flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
              : 'flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm text-destructive'
          }
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {deal.status === 'WON' ? (
            <span>
              Won on {formatDate(deal.actualCloseDate)} for {formatMoney(deal.value, deal.currency)}
            </span>
          ) : (
            <span>
              Lost on {formatDate(deal.actualCloseDate)} — {deal.lostReason}
              {deal.competitor ? ` (to ${deal.competitor})` : ''}
              {deal.lostNote ? ` · ${deal.lostNote}` : ''}
            </span>
          )}
        </div>
      )}

      {deal.isRotting && deal.status === 'OPEN' && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          No activity since {timeAgo(deal.lastActivityAt)} — past the threshold for {deal.stage.name}.
        </div>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[19rem_minmax(0,1fr)] xl:grid-cols-[20rem_minmax(0,1fr)_19rem]">
        <aside className="space-y-3 lg:sticky lg:top-0">
          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <Link to="/crm/deals" className="text-link inline-flex items-center gap-1 text-sm font-semibold hover:underline">
                <ChevronLeft className="h-4 w-4" />
                {t('Deals')}
              </Link>
              <Can permission="crm.deal.update">
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="h-4 w-4" />
                  {t('Edit')}
                </Button>
              </Can>
            </div>
            <div className="p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Briefcase className="h-6 w-6" />
                </span>
                <div className="min-w-0">
                  <h1 className="text-xl font-bold leading-tight [overflow-wrap:anywhere]">{deal.title}</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {deal.number} · {DEAL_TYPE_LABELS[deal.dealType]}
                  </p>
                </div>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 rounded-md bg-muted/60 p-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">{t('Amount')}</dt>
                  <dd className="tabular font-bold">{formatMoney(deal.value, deal.currency)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('Close date')}</dt>
                  <dd className="font-semibold">{formatDate(deal.expectedCloseDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('Stage')}</dt>
                  <dd className="truncate font-semibold">{deal.stage.name}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('Pipeline')}</dt>
                  <dd className="truncate font-semibold">{deal.pipeline.name}</dd>
                </div>
              </dl>
              <div className="mt-3">
                <RecordTags type="deal" id={deal.id} canEdit={can('crm.deal.update')} />
              </div>
              <div className="mt-4">
                <SalesToolbar
                  variant="round"
                  record={{
                    entityType: 'deal',
                    entityId: deal.id,
                    name: deal.contact ? [deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ') : deal.company.name,
                    email: deal.contact?.email ?? null,
                  }}
                />
              </div>
            </div>
          </div>

          <RecordSection title="About this deal">
            <div className="space-y-3 pt-2 text-sm">
              {deal.recurringValue && (
                <Detail
                  label="Recurring"
                  value={`${formatMoney(deal.recurringValue, deal.currency)}${deal.billingCycle ? ` / ${deal.billingCycle.toLowerCase()}` : ''}`}
                />
              )}
              <Detail label="Weighted" value={formatMoney(deal.weightedValue, deal.currency)} />
              <Detail label="Probability" value={`${deal.probability ?? deal.stage.probability}%`} />
              <Detail label="Source" value={deal.source ? LEAD_SOURCE_LABELS[deal.source] : '—'} />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Owner</span>
                {deal.owner ? (
                  <span className="flex items-center gap-1.5">
                    <Avatar name={deal.owner.name} size={20} />
                    <span className="text-sm">{deal.owner.name}</span>
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                )}
              </div>
              <Detail label="Created" value={formatDate(deal.createdAt)} />
            </div>
          </RecordSection>
          <CustomPropertiesSection type="deal" id={deal.id} values={deal.customFields} canEdit={can('crm.deal.update')} />
          <div className="px-1">
            <HistoryButton type="deal" id={deal.id} />
          </div>
        </aside>

        <div className="min-w-0">
          <div className="mb-3 flex rounded-lg border bg-card p-1">
            {(['activities', 'overview'] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={cn(
                  'flex-1 rounded-md py-2 text-sm font-semibold transition-colors',
                  tab === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {key === 'overview' ? t('Sales tools & insights') : t('Activities')}
              </button>
            ))}
          </div>
          <div className="space-y-4">
            {tab === 'activities' ? (
              <TimelinePanel entityType="deal" entityId={deal.id} />
            ) : (
              <>
                <AiRecordSummary entityType="deal" entityId={deal.id} />
                <RecordSalesActivity entityType="deal" entityId={deal.id} />
                <RecordSection title="Stage history" count={deal.stageHistory.length}>
                  <ol className="space-y-3 pt-3">
                    {deal.stageHistory.map((entry) => (
                      <li key={entry.id} className="flex items-start gap-3 text-sm">
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: entry.toStage.colour }} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {entry.fromStage && (
                              <>
                                <span className="text-muted-foreground">{entry.fromStage.name}</span>
                                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              </>
                            )}
                            <span className="font-medium">{entry.toStage.name}</span>
                            {entry.durationSeconds !== null && (
                              <span className="text-xs text-muted-foreground">after {formatDuration(entry.durationSeconds)}</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatDateTime(entry.changedAt)}
                            {entry.changedBy ? ` · ${entry.changedBy.name}` : ''}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </RecordSection>
              </>
            )}
          </div>
        </div>

        <aside className="space-y-3 lg:col-span-2 xl:col-span-1">
          <RecordSection title="Companies" count={1}>
            <Link to={`/crm/companies/${deal.companyId}`} className="mt-2 block rounded-md border p-2.5 text-sm hover:bg-accent">
              <span className="text-link font-semibold">{deal.company.name}</span>
            </Link>
          </RecordSection>
          <RecordSection title="Contacts" count={deal.contact ? 1 : 0}>
            {deal.contact ? (
              <Link to={`/crm/contacts/${deal.contact.id}`} className="mt-2 flex items-center gap-2.5 rounded-md border p-2.5 text-sm hover:bg-accent">
                <Avatar name={[deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ')} size={28} />
                <span className="min-w-0">
                  <span className="text-link block truncate font-semibold">
                    {[deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ')}
                  </span>
                  {deal.contact.email && <span className="block truncate text-xs text-muted-foreground">{deal.contact.email}</span>}
                </span>
              </Link>
            ) : (
              <p className="py-3 text-sm text-muted-foreground">No contact on this deal.</p>
            )}
          </RecordSection>
          <RecordSection
            title="Quotes"
            count={deal.quotes.length}
            action={
              <Can permission="crm.quote.create">
                <Link to={`/crm/quotes/new?dealId=${deal.id}&companyId=${deal.companyId}`} className="text-link text-xs font-semibold hover:underline">
                  + {t('Add')}
                </Link>
              </Can>
            }
          >
            {deal.quotes.length === 0 ? (
              <p className="py-3 text-sm text-muted-foreground">No quotes yet.</p>
            ) : (
              <ul className="space-y-2 pt-2">
                {deal.quotes.map((quote) => (
                  <li key={quote.id}>
                    <Link to={`/crm/quotes/${quote.id}`} className="block rounded-md border p-2.5 text-sm hover:bg-accent">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-link font-semibold">
                          {quote.number}
                          {quote.version > 1 && <span className="text-muted-foreground"> v{quote.version}</span>}
                        </span>
                        <Badge variant={QUOTE_STATUS_VARIANT[quote.status]}>{QUOTE_STATUS_LABELS[quote.status]}</Badge>
                      </span>
                      <span className="tabular text-xs text-muted-foreground">{formatMoney(quote.grandTotal, quote.currency)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </RecordSection>
          <AssociationsSection type="deal" id={deal.id} />
          <LinkedObjectsCard entityType="deal" entityId={deal.id} />
        </aside>
      </div>

      {editing && <DealDialog deal={deal} onClose={() => setEditing(false)} />}
      {movingTo && (
        <MoveDealDialog
          deal={deal}
          targetStage={movingTo}
          currentStage={stages.find((stage) => stage.id === deal.stageId)}
          onClose={() => setMovingTo(null)}
        />
      )}
    </div>
  );
}

const CALL_OUTCOME_FROM_TIMELINE: Record<string, string> = {
  connected: 'CONNECTED',
  no_answer: 'NO_ANSWER',
  busy: 'BUSY',
  wrong_number: 'WRONG_NUMBER',
  callback: 'CALLBACK',
};

/** Notes, calls, meetings and tasks against one record (spec §5.6). */
export function TimelinePanel({
  entityType,
  entityId,
}: {
  entityType: string;
  entityId: string;
}) {
  const { data } = useTimeline(entityType, entityId);
  const log = useLogActivity();
  const logCall = useLogCall();
  const saveTask = useSaveTask();

  const [mode, setMode] = useState<'note' | 'call' | 'task'>('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [outcome, setOutcome] = useState('connected');
  const [duration, setDuration] = useState('');
  const [dueAt, setDueAt] = useState('');

  const submit = async () => {
    if (title.trim().length === 0) return;

    if (mode === 'task') {
      await saveTask.mutateAsync({ title, description: body, entityType, entityId, dueAt, type: 'FOLLOW_UP' });
    } else if (mode === 'call' && ['lead', 'contact', 'company', 'deal'].includes(entityType)) {
      // A real call record, so it counts in the activity report.
      await logCall.mutateAsync({
        entityType,
        entityId,
        outcome: CALL_OUTCOME_FROM_TIMELINE[outcome] ?? 'CONNECTED',
        durationSeconds: duration ? Math.round(Number(duration) * 60) : 0,
        notes: [title, body].filter(Boolean).join('\n\n'),
      });
    } else {
      await log.mutateAsync({
        entityType,
        entityId,
        type: mode === 'call' ? 'CALL' : 'NOTE',
        title,
        body,
        ...(mode === 'call'
          ? { meta: { outcome, durationMinutes: duration ? Number(duration) : undefined } }
          : {}),
      });
    }
    setTitle('');
    setBody('');
    setDuration('');
    setDueAt('');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Activity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border p-3">
          <div className="mb-2 flex gap-1">
            {(
              [
                ['note', 'Note', StickyNote],
                ['call', 'Log a call', Phone],
                ['task', 'Task', Plus],
              ] as const
            ).map(([value, label, Icon]) => (
              <button
                key={value}
                onClick={() => setMode(value)}
                className={
                  mode === value
                    ? 'flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground'
                    : 'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent'
                }
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>

          <Input
            value={title}
            placeholder={
              mode === 'call'
                ? 'What was discussed?'
                : mode === 'task'
                  ? 'What needs doing?'
                  : 'Add a note'
            }
            onChange={(event) => setTitle(event.target.value)}
          />

          {mode === 'call' && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <Select value={outcome} onChange={(event) => setOutcome(event.target.value)}>
                <option value="connected">Connected</option>
                <option value="no_answer">No answer</option>
                <option value="busy">Busy</option>
                <option value="wrong_number">Wrong number</option>
                <option value="callback">Callback requested</option>
              </Select>
              <Input
                type="number"
                min={0}
                placeholder="Minutes"
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
              />
            </div>
          )}

          {mode === 'task' && (
            <div className="mt-2 space-y-1.5">
              <Label className="text-xs">Due</Label>
              <Input
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </div>
          )}

          <Textarea
            className="mt-2"
            rows={2}
            value={body}
            placeholder="More detail (optional)"
            onChange={(event) => setBody(event.target.value)}
          />

          <Button
            size="sm"
            className="mt-2"
            onClick={submit}
            disabled={title.trim().length === 0 || log.isPending || saveTask.isPending}
          >
            {log.isPending || saveTask.isPending ? 'Saving…' : 'Add'}
          </Button>
        </div>

        {data?.tasks.filter((task) => task.status !== 'DONE').length ? (
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Open tasks</div>
            <ul className="space-y-1.5">
              {data.tasks
                .filter((task) => task.status !== 'DONE')
                .map((task) => (
                  <li
                    key={task.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{task.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {TASK_TYPE_LABELS[task.type]}
                        {task.dueAt ? ` · due ${formatDate(task.dueAt)}` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge variant={TASK_PRIORITY_VARIANT[task.priority]} className="text-[10px]">
                        {task.priority}
                      </Badge>
                      <button
                        onClick={() => saveTask.mutate({ id: task.id, status: 'DONE' })}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        Done
                      </button>
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        ) : null}

        <ol className="space-y-3">
          {(data?.activities ?? []).map((activity) => (
            <li key={activity.id} className="flex items-start gap-2.5">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
              <div className="min-w-0 flex-1">
                <div className="text-sm">{activity.title}</div>
                {activity.body && (
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-muted-foreground">
                    {activity.body}
                  </p>
                )}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {activity.actor?.name ?? 'System'} · {timeAgo(activity.occurredAt)}
                </div>
              </div>
            </li>
          ))}
          {data?.activities.length === 0 && (
            <li className="text-sm text-muted-foreground">Nothing logged yet.</li>
          )}
        </ol>
      </CardContent>
    </Card>
  );
}

function Detail({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={strong ? 'tabular font-semibold' : 'tabular text-sm'}>{value}</span>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'}`;
  const hours = Math.floor(seconds / 3600);
  if (hours > 0) return `${hours}h`;
  return `${Math.max(1, Math.floor(seconds / 60))}m`;
}
